import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { handleText } from './SingleSelectButton';

export interface SingleSelectTextFieldProps {
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLInputElement | HTMLButtonElement | null>>;
  anchorEl: HTMLInputElement | HTMLButtonElement | null;
  controllerName: string;
  resetEnabled: boolean;
  handleReset: (isFullRest: boolean) => void;
  useAsDateComparison?: boolean;
  selectedLabel: string;
  textFieldLabel?: string;
  isRequired?: boolean;
}

export interface TextFieldInputAdornmentProps {
  textFieldType: string;
  controllerName: string;
  resetEnabled: boolean;
  handleReset: (isFullRest: boolean) => void;
  open: boolean;
}

export function TextFieldInputAdornment({
  textFieldType,
  resetEnabled,
  controllerName,
  handleReset,
  open,
}: Readonly<TextFieldInputAdornmentProps>) {
  return (
    <InputAdornment
      position='end'
      sx={{
        '.reset-btn': {
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 0.15s',
        },
        '.MuiInputBase-root:hover & .reset-btn, .MuiInputBase-root:focus-within & .reset-btn': {
          opacity: 1,
          pointerEvents: 'auto',
        },
      }}
    >
      {resetEnabled && (
        <IconButton
          className='reset-btn'
          data-testid={`${controllerName}-${textFieldType}-reset-button`}
          sx={{ width: 28, height: 28, marginRight: '-2px', padding: '4px' }}
          onClick={(e) => {
            e.stopPropagation();
            handleReset(true);
          }}
        >
          <CloseOutlinedIcon sx={{ fontSize: '20px' }} />
        </IconButton>
      )}
      <IconButton sx={{ width: 28, height: 28 }}>
        {open ? <ArrowDropUpOutlinedIcon fontSize='inherit' /> : <ArrowDropDownOutlinedIcon fontSize='inherit' />}
      </IconButton>
    </InputAdornment>
  );
}

function SingleSelectTextField({
  setAnchorEl,
  anchorEl,
  controllerName,
  resetEnabled,
  handleReset,
  selectedLabel,
  useAsDateComparison,
  textFieldLabel,
  isRequired,
}: Readonly<SingleSelectTextFieldProps>) {
  const { t } = useTranslation();
  const form = useFormContext();
  const {
    getValues,
    formState: { errors },
  } = form;
  const error = errors[controllerName];
  const errorMessage = typeof error?.message === 'string' ? error.message : '';

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);

  return (
    <TextField
      fullWidth
      label={textFieldLabel}
      data-testid={`${controllerName}-single-select-text-field`}
      variant='standard'
      onClick={handleClick}
      value={handleText(t, useAsDateComparison || false, getValues, controllerName, selectedLabel, false, anchorEl, '')}
      required={isRequired}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <TextFieldInputAdornment
            textFieldType='single-select'
            resetEnabled={resetEnabled}
            controllerName={controllerName}
            handleReset={handleReset}
            open={open}
          />
        ),
      }}
      error={!!error}
      helperText={errorMessage}
    />
  );
}

SingleSelectTextField.defaultProps = {
  useAsDateComparison: false,
  textFieldLabel: '',
  isRequired: false,
};

export default SingleSelectTextField;
